import React from "react"
import { View, ViewStyle } from "react-native"

import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from "react-native-reanimated"

import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"

// How long between each dot starting to illuminate (the left-to-right stagger).
const DOT_STAGGER = 180
// One full cycle: each of the three dots gets its own slot.
const DOT_PERIOD = DOT_STAGGER * 3
// Dimmed opacity for a dot that is not currently illuminated.
const DOT_DIM = 0.3

interface ThinkingDotsProps {
  dotSize?: number
  dotSpacing?: number
  color?: string
}

export const ThinkingDots = (props: ThinkingDotsProps) => {
  const { themed } = useAppTheme()
  const { dotSize = 4, dotSpacing = 2, color } = props

  const dot1Opacity = useSharedValue(DOT_DIM)
  const dot2Opacity = useSharedValue(DOT_DIM)
  const dot3Opacity = useSharedValue(DOT_DIM)

  const dot1Style = useAnimatedStyle(() => ({
    opacity: dot1Opacity.value,
  }))
  const dot2Style = useAnimatedStyle(() => ({
    opacity: dot2Opacity.value,
  }))
  const dot3Style = useAnimatedStyle(() => ({
    opacity: dot3Opacity.value,
  }))

  React.useEffect(() => {
    // Each dot illuminates briefly within its own slot, then dims while the
    // remaining dots take their turn. The total sequence length (DOT_PERIOD) is
    // identical for every dot, so the staggered start offsets are preserved on
    // every repeat — producing a clean left-to-right wave.
    const makePulse = () =>
      withRepeat(
        withSequence(
          withTiming(1, { duration: DOT_STAGGER / 2, easing: Easing.inOut(Easing.ease) }),
          withTiming(DOT_DIM, { duration: DOT_STAGGER / 2, easing: Easing.inOut(Easing.ease) }),
          // Stay dim until this dot's slot comes around again.
          withTiming(DOT_DIM, { duration: DOT_PERIOD - DOT_STAGGER }),
        ),
        -1,
      )

    dot1Opacity.value = makePulse()
    dot2Opacity.value = withDelay(DOT_STAGGER, makePulse())
    dot3Opacity.value = withDelay(DOT_STAGGER * 2, makePulse())
  }, [dot1Opacity, dot2Opacity, dot3Opacity])

  const dotStyle = React.useMemo(() => {
    const baseStyle = themed($dot)
    return {
      ...baseStyle,
      height: dotSize,
      width: dotSize,
      marginHorizontal: dotSpacing,
      borderRadius: dotSize / 2,
      ...(color && { backgroundColor: color }),
    }
  }, [themed, dotSize, dotSpacing, color])

  return (
    <View style={$dotsContainer}>
      <Animated.View style={[dotStyle, dot1Style]} />
      <Animated.View style={[dotStyle, dot2Style]} />
      <Animated.View style={[dotStyle, dot3Style]} />
    </View>
  )
}

const $dot: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.text,
})

const $dotsContainer: ViewStyle = {
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "center",
}
